import { ParsedUrlQuery } from 'querystring';
import { OpenAPI } from '@rbx/docsite-utilities/dist/browser';
import { OpenAPIPageContent } from '@modules/utilities/pageBuild/content';
import { TTheme } from '@rbx/ui';
import { AuthTypeValue, getAuthenticationTypes } from './getAuthenticationTypes';
import { extractDomain, getFirstBaseUrl } from './serverUtils';
import { sortOperations, sortDomains, sortStabilities } from './sort';
import { getAuthTypeConfig } from '../components/AuthChip';
import { getStabilityConfig } from '../components/StabilityChip';

export enum FilterOptionType {
  Auth = 'auth',
  Stability = 'stability',
  Domain = 'domain',
}

export type FilterOption =
  | {
      type: FilterOptionType.Stability;
      value: NonNullable<OpenAPI.OperationObject['x-roblox-stability']>;
    }
  | {
      type: FilterOptionType.Auth;
      value: AuthTypeValue;
    }
  | {
      type: FilterOptionType.Domain;
      value: string;
    };

export const getAvailableFilterOptions = (
  document: OpenAPI.Document,
  theme: TTheme,
): FilterOption[] => {
  // Build sets from operations
  const authTypes = new Set<AuthTypeValue>();
  const stabilities = new Set<NonNullable<OpenAPI.OperationObject['x-roblox-stability']>>();
  const domains = new Set<string>();

  OpenAPI.walkOperations(document, (operation: OpenAPI.OperationObject) => {
    const operationAuthTypes = getAuthenticationTypes(operation, document);
    operationAuthTypes.forEach((authType) => authTypes.add(authType));
    const stability = operation['x-roblox-stability'];
    if (stability) {
      stabilities.add(stability);
    }
    const baseUrl = getFirstBaseUrl(operation, document);
    const domain = baseUrl ? extractDomain(baseUrl) : null;
    if (domain) {
      domains.add(domain);
    }
  });

  // Build filter options from sets
  const filterOptions: FilterOption[] = [];

  // Already sorted by the getAuthenticationTypes
  authTypes.forEach((authType) => {
    const config = getAuthTypeConfig(authType, theme);
    if (config) {
      filterOptions.push({
        type: FilterOptionType.Auth,
        value: authType,
      });
    }
  });

  const sortedStabilities = sortStabilities(Array.from(stabilities));
  sortedStabilities.forEach((stability) => {
    const config = getStabilityConfig(stability, theme);
    if (config) {
      filterOptions.push({
        type: FilterOptionType.Stability,
        value: stability,
      });
    }
  });

  const sortedDomains = sortDomains(Array.from(domains));
  sortedDomains.forEach((domain) => {
    filterOptions.push({
      type: FilterOptionType.Domain,
      value: domain,
    });
  });

  return filterOptions;
};

export const getFilteredOperations = (
  document: OpenAPI.Document,
  content: OpenAPIPageContent,
  selectedFilterOptions: FilterOption[],
): Array<{
  operation: OpenAPI.OperationObject;
  method: OpenAPI.HttpMethod;
  path: string;
}> => {
  // Build sets from selected filter options
  const authTypes = new Set<AuthTypeValue>();
  const stabilities = new Set<NonNullable<OpenAPI.OperationObject['x-roblox-stability']>>();
  const domains = new Set<string>();

  selectedFilterOptions.forEach((option) => {
    if (option.type === FilterOptionType.Auth) {
      authTypes.add(option.value);
    } else if (option.type === FilterOptionType.Stability) {
      stabilities.add(option.value);
    } else if (option.type === FilterOptionType.Domain) {
      domains.add(option.value);
    }
  });

  // Filter operations by sets
  const filteredOps: Array<{
    operation: OpenAPI.OperationObject;
    method: OpenAPI.HttpMethod;
    path: string;
  }> = [];

  OpenAPI.walkOperations(
    document,
    (operation: OpenAPI.OperationObject, method: OpenAPI.HttpMethod, operationPath: string) => {
      const sanitizedPath = OpenAPI.sanitizePath(operationPath);
      const baseUrl = getFirstBaseUrl(operation, document);
      const domain = baseUrl ? extractDomain(baseUrl) : null;
      const operationAuthTypes = getAuthenticationTypes(operation, document);
      const stability = operation['x-roblox-stability'];

      if (selectedFilterOptions.length > 0) {
        if (authTypes.size > 0 && !operationAuthTypes.some((authType) => authTypes.has(authType))) {
          return;
        }

        if (stabilities.size > 0 && (!stability || !stabilities.has(stability))) {
          return;
        }

        if (domains.size > 0 && (!domain || !domains.has(domain))) {
          return;
        }
      }

      filteredOps.push({
        operation,
        method,
        path: sanitizedPath,
      });
    },
  );

  return sortOperations(
    filteredOps,
    document,
    content.config.type as 'feature' | 'domain',
    content.config.key,
  );
};

/**
 * Shortens a domain name for URL serialization
 */
const shortenDomain = (domain: string): string => {
  if (domain.endsWith('.roblox.com')) {
    return domain.split('.')[0];
  }
  return domain;
};

/**
 * Expands a shortened domain name back to full domain
 */
const expandDomain = (shortened: string, availableOptions: FilterOption[]): string | null => {
  const availableDomains = availableOptions
    .filter((opt) => opt.type === FilterOptionType.Domain)
    .map((opt) => opt.value);

  const match = availableDomains.find((domain) => domain.split('.')[0] === shortened);

  return match ?? null;
};

export const filterOptionsToQueryParams = (
  filterOptions: FilterOption[],
): Record<string, string> => {
  const queryParams: Record<string, string[]> = {
    [FilterOptionType.Auth]: [],
    [FilterOptionType.Stability]: [],
    [FilterOptionType.Domain]: [],
  };

  filterOptions.forEach((option) => {
    let value: string;
    if (option.type === FilterOptionType.Domain) {
      value = shortenDomain(option.value);
    } else {
      value = option.value;
    }
    queryParams[option.type].push(value);
  });

  const result: Record<string, string> = {};
  Object.entries(queryParams).forEach(([key, values]) => {
    if (values.length > 0) {
      result[key] = values.join(',');
    }
  });

  return result;
};

export const queryParamsToFilterOptions = (
  query: ParsedUrlQuery,
  availableOptions: FilterOption[],
): FilterOption[] => {
  const result: FilterOption[] = [];

  // Helper to split comma-separated values
  const splitValues = (param: string | string[] | undefined): string[] => {
    if (!param) {
      return [];
    }
    if (Array.isArray(param)) {
      return param.flatMap((p) => (typeof p === 'string' ? p.split(',') : []));
    }
    return param.split(',');
  };

  const authValues = splitValues(query[FilterOptionType.Auth]);
  authValues.forEach((value) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      const match = availableOptions.find(
        (opt) => opt.type === FilterOptionType.Auth && opt.value === trimmedValue,
      );
      if (match) {
        result.push(match);
      }
    }
  });

  const stabilityValues = splitValues(query[FilterOptionType.Stability]);
  stabilityValues.forEach((value) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      const match = availableOptions.find(
        (opt) => opt.type === FilterOptionType.Stability && opt.value === trimmedValue,
      );
      if (match) {
        result.push(match);
      }
    }
  });

  const domainValues = splitValues(query[FilterOptionType.Domain]);
  domainValues.forEach((value) => {
    const trimmedValue = value.trim();
    if (trimmedValue) {
      const expandedDomain = expandDomain(trimmedValue, availableOptions);
      if (expandedDomain) {
        const match = availableOptions.find(
          (opt) => opt.type === FilterOptionType.Domain && opt.value === expandedDomain,
        );
        if (match) {
          result.push(match);
        }
      }
    }
  });

  return result;
};
